import { Edit2, Eye, PenTool, Plus, Trash2, UserIcon } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppButton from "../../components/ui/AppButton";
import DashboardFilterBar, {
  ALL_OPTION,
  filterSelectClass,
  isAllFilter,
  stringFromFilter,
} from "../../components/ui/dashboard/DashboardFilterBar";
import Pagination from "../../components/ui/dashboard/Pagination";
import Table, { Column } from "../../components/ui/dashboard/Table";
import ThreeDotsMenu from "../../components/ui/ThreeDotsMenu";
import useDebouncedValue from "../../hooks/useDebouncedValue";
import { usePermissions } from "../../hooks/usePermissions";
import { buildApiUrl, cn, docFormatDate } from "../../lib/utils";
import {
  VehicleAssignment,
  VehicleAssignmentSearchRequest,
  VehicleAssignmentSearchResponse,
} from "./@types";
import UnassignVehicle from "./components/UnassignVehicle";
import { useGetVehicleAssignmentsMutation } from "./vehicleApiSlice";
import VehicleAssignmentModal from "./VehicleAssignmentModal";
import VehicleTypesDropdown from "./ui/VehicleTypesDropdown";

const ASSIGNMENT_STATUS_OPTIONS = [
  ALL_OPTION,
  { value: "Active", label: "Active" },
  { value: "Completed", label: "Completed" },
];

const VehicleAssignments: React.FC = () => {
  const navigate = useNavigate();
  const { canCreate, canUpdate, canDelete } = usePermissions();
  const [searchTerm, setSearchTerm] = useState("");
  const [vehicleTypeFilter, setVehicleTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const [selectedVA, setSelectedCM] = useState<VehicleAssignment | null>(null);
  const [openModal, setOpenModal] = useState<string | null>(null);
  const debouncedKeyword = useDebouncedValue(searchTerm.trim());

  const [getVehicleAssignments, { data: driverData, isLoading, error: loadError }] =
    useGetVehicleAssignmentsMutation();

  const fetchCM = useCallback(async () => {
    const requestBody: VehicleAssignmentSearchRequest = {
      pageNumber: currentPage,
      pageSize,
      keyword: debouncedKeyword || undefined,
      vehicleTypes: stringFromFilter(vehicleTypeFilter),
      status: stringFromFilter(statusFilter),
    };

    try {
      await getVehicleAssignments(requestBody).unwrap();
    } catch (err) {
      console.error("Failed to fetch assigned vehicles:", err);
    }
  }, [
    currentPage,
    pageSize,
    debouncedKeyword,
    vehicleTypeFilter,
    statusFilter,
    getVehicleAssignments,
  ]);

  useEffect(() => {
    fetchCM();
  }, [fetchCM]);

  useEffect(() => {
    setCurrentPage(0);
  }, [debouncedKeyword, vehicleTypeFilter, statusFilter]);

  // Table columns configuration
  const columns: Column<VehicleAssignment>[] = [
    {
      key: "id",
      header: "Vehicle ID",
      width: "",
      render: (value: string) => (
        <span className="text-hColor capitalize">{value.slice(0, 4)}</span>
      ),
    },
    {
      key: "vehicleName",
      header: "Vehicle assigned",

      render: (value: string, item: VehicleAssignment) => (
        <span className="text-hColor capitalize">
          {value} {item.vehicleName} {item.vehicleModel}
        </span>
      ),
    },
    {
      key: "vehicleType",
      header: "Vehicle type",

      render: (value: string) => <span className="text-hColor">{value}</span>,
    },
    {
      key: "driver",
      header: "Assigned driver",

      render: (value: string, item: VehicleAssignment) => (
        <div className="flex items-center space-x-3">
          {item.driver.imageUrl ? (
            <img
              src={buildApiUrl(item.driver.imageUrl)}
              alt={`${item.driver.fullName}`}
              className="w-[30px] h-[30px] rounded-full object-cover border border-gray-300 shadow-sm"
            />
          ) : (
            <div className="w-[30px] h-[30px] rounded-full bg-gray-200 flex items-center justify-center border border-gray-300 shadow-sm p-1">
              <UserIcon className="w-full h-full text-hColor" />
            </div>
          )}
          <div className="flex flex-col">
            <span className="text-hColor capitalize">
              {item.driver.fullName}
            </span>
            <span className="text-hColor text-sm">{item.driver.email}</span>
          </div>
        </div>
      ),
    },
    {
      key: "startDate",
      header: "Start date",

      render: (value: string) => (
        <span className="text-hColor">{docFormatDate(value)}</span>
      ),
    },
    {
      key: "endDate",
      header: "End date",

      render: (value: string) => (
        <span className="text-hColor">{docFormatDate(value)}</span>
      ),
    },
    {
      key: "actions",
      header: "Action",

      align: "center" as const,
      render: (value: any, item: VehicleAssignment) => {
        return (
          <ThreeDotsMenu
            actions={[
              {
                label: "View vehicle",
                onClick: () =>
                  navigate(
                    `/admin/vehicles/${item.vehicleId || item.vehicle?.id}`,
                  ),
                icon: <Eye className="w-4 h-4" />,
              },
              ...(canUpdate("VehicleAssignments")
                ? [
                    {
                      label: "Edit",
                      onClick: () => {
                        setOpenModal("edit");
                        setSelectedCM(item);
                      },
                      icon: <Edit2 className="w-4 h-4" />,
                    },
                    {
                      label: "Unassign vehicle",
                      onClick: () => {
                        setOpenModal("unassign");
                        setSelectedCM(item);
                      },
                      icon: <PenTool className="w-4 h-4" />,
                    },
                  ]
                : []),
              ...(canDelete("VehicleAssignments")
                ? [
                    {
                      label: "Delete assignment",
                      onClick: () => {
                        setOpenModal("delete");
                        setSelectedCM(item);
                      },
                      icon: <Trash2 className="w-4 h-4" />,
                    },
                  ]
                : []),
            ]}
          />
        );
      },
    },
  ];

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(0);
  };

  const handleModal = () => {
    setOpenModal(null);
    fetchCM();
  };

  const activeFilters = useMemo(() => {
    const chips: { key: string; label: string; onRemove: () => void }[] = [];
    if (debouncedKeyword) {
      chips.push({
        key: "keyword",
        label: `Search: ${debouncedKeyword}`,
        onRemove: () => setSearchTerm(""),
      });
    }
    if (!isAllFilter(vehicleTypeFilter)) {
      chips.push({
        key: "type",
        label: `Type: ${vehicleTypeFilter}`,
        onRemove: () => setVehicleTypeFilter("all"),
      });
    }
    if (!isAllFilter(statusFilter)) {
      chips.push({
        key: "status",
        label: `Status: ${statusFilter}`,
        onRemove: () => setStatusFilter("all"),
      });
    }
    return chips;
  }, [debouncedKeyword, vehicleTypeFilter, statusFilter]);

  const response = driverData?.data as
    | VehicleAssignmentSearchResponse
    | undefined;

  return (
    <div className="min-h-screen py-6 w-full bg-[#F9F9F9]">
      <div className="max-w-full lg:max-w-[80vw] mx-auto space-y-10">
        <div className="flex flex-wrap items-center justify-between gap-3 bg-white rounded-[10px] p-4">
          <div className="">
            <h2 className="text-xl font-semibold text-hColor">
              Vehicle assignment
            </h2>
          </div>

          {canCreate("VehicleAssignments") && (
            <div className="flex gap-3">
              <AppButton
                onClick={() => setOpenModal("add")}
                className="flex items-center px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/60 transition-colors h-[38px]"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add assignment
              </AppButton>
            </div>
          )}
        </div>

        {/* Assigned vehicles section */}
        <div className="space-y-4 bg-white rounded-[10px] p-4">
          <DashboardFilterBar
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            onClearSearch={() => setSearchTerm("")}
            searchPlaceholder="Search by vehicle or driver"
            isLoading={isLoading}
            extraFilters={
              <VehicleTypesDropdown
                showLabel={false}
                includeAll
                showSearch
                value={vehicleTypeFilter}
                onChange={(value) => setVehicleTypeFilter(value || "all")}
                placeholder="Vehicle type"
                containerClassName="w-auto"
                className={cn(filterSelectClass, "w-full sm:w-[160px]")}
              />
            }
            filters={[
              {
                id: "status",
                placeholder: "Status",
                value: statusFilter,
                options: ASSIGNMENT_STATUS_OPTIONS,
                onChange: (value) => setStatusFilter(value || "all"),
              },
            ]}
            chips={activeFilters}
            onClearAll={() => {
              setSearchTerm("");
              setVehicleTypeFilter("all");
              setStatusFilter("all");
            }}
            resultCount={response?.totalCount}
            resultLabel={
              response?.totalCount === 1 ? "assignment" : "assignments"
            }
          />
            <Table
              data={response?.data || []}
              columns={columns}
              loading={isLoading}
              error={loadError}
              emptyMessage="No assigned vehicles found"
            />
        </div>

        <div className="">
          {/* Pagination */}
          {response && (
            <Pagination
              currentPage={response.currentPage}
              totalPages={response.totalPages}
              pageSize={response.pageSize}
              totalCount={response.totalCount}
              hasNextPage={response.hasNextPage}
              hasPreviousPage={response.hasPreviousPage}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
            />
          )}
        </div>
      </div>

      <VehicleAssignmentModal
        isOpen={openModal === "add" || openModal === "edit"}
        onClose={() => handleModal()}
        vehicleAssignment={selectedVA ? selectedVA : null}
      />

      {selectedVA && (
        <UnassignVehicle
          vehicle={selectedVA}
          isOpen={openModal === "unassign" || openModal === "delete"}
          onClose={() => handleModal()}
          mode={openModal === "delete" ? "delete" : "unassign"}
        />
      )}
    </div>
  );
};

export default VehicleAssignments;
